import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { CreditDocument, Document } from '../documents/document';
import { DocumentGenerationInput } from '../documents/document-generation-input';
import { UploadFile } from '../documents/upload-file';
import { DocumentDownload } from "../model/document-download";
import { EncompassTreeModel } from '../model/encompass-tree-model';
import { EncompassTreeNode } from '../model/encompass-tree-node';
import { GoogleService } from './google-service';
import { EntityKey } from '../entity/entity';

@Service()
export class DocumentService {
  private readonly http = inject(HttpClient);
  private readonly googleService = inject(GoogleService);


  private urlBase = environment.serviceUrl;

  public getEntityDocuments(entityId: string, entityRevision: number): Observable<Document[]> {
    let url = this.urlBase + 'documents/' + entityId + '/' + entityRevision;
    return this.http.get<Document[]>(url, {headers: this.getRequestHeaders()});
  }

  public downloadDocument(target: Document): Observable<Blob> {
    let url = this.urlBase + 'documents/download';
    return this.downloadViaPost(url, target);
  }

  public uploadFiles(files: UploadFile[]): Observable<string[]> {
    const url = this.urlBase + 'documents/upload/';
    const observables: Observable<string>[] = [];
    for (const file of files) {
      let entityId: string = file.entityKey.entityId;
      let entityRevision: number = file.entityKey.entityRevision;
      let uploadUrl = url + entityId + '/' + entityRevision + '?fileName=' + encodeURIComponent(file.fileName) + '&mimeType=' + encodeURIComponent(file.mimeType) + '&documentName=' + encodeURIComponent(file.documentName) + '&documentType=' + encodeURIComponent(file.documentType);
      let body = file.fileBytes;
      let headers: HttpHeaders = this.getRequestHeaders().set("Content-Type", file.mimeType);
      observables.push(this.http.post(uploadUrl, body, {headers: headers, responseType: 'text'}));
    }
    return forkJoin(observables);
  }

  public generate(input: DocumentGenerationInput): Observable<string> {
    const url: string = this.urlBase + 'documents/generate';
    return this.http.post(url, JSON.stringify(input), {headers: this.getPostHeaders(), responseType: 'text'});
  }

  public generateProjectSubmittal(input: DocumentGenerationInput): Observable<string> {
    const url: string = this.urlBase + 'documents/generateProjectSubmittal';
    return this.http.post(url, JSON.stringify(input), {headers: this.getPostHeaders(), responseType: 'text'});
  }

  public getTrainingDocuments(brandName: string): Observable<Document[]> {
    let url = this.urlBase + 'documents/training/' + brandName;
    return this.http.get<Document[]>(url, {headers: this.getRequestHeaders()});
  }

  public downloadTrainingDocument(target: Document): Observable<Blob> {
    let url = this.urlBase + 'documents/training/download';
    return this.downloadViaPost(url, target);
  }

  public getMarketingDocuments(brandName: string): Observable<EncompassTreeModel> {
    let url = this.urlBase + 'documents/marketing/' + brandName;
    return this.http.get<EncompassTreeModel>(url, {headers: this.getRequestHeaders()});
  }

  public downloadMarketingDocument(documentId: number): Observable<Blob> {
    let url = this.urlBase + 'documents/marketing/download?documentId=' + documentId;
    let headers: HttpHeaders = this.getRequestHeaders().set("Accept", "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8");
    return this.downloadViaGet(url, headers);
  }

  public downloadSupportDocument(brandName: string, documentType: string, fileName: string): Observable<DocumentDownload> {
    const url: string = this.urlBase + 'documents/supportDoc/' + encodeURIComponent(brandName) + '/' + encodeURIComponent(documentType) + "?fileName=" + encodeURIComponent(fileName);
    let headers: HttpHeaders = this.getRequestHeaders().set("Accept", "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8");
    return this.http.get<DocumentDownload>(url, {headers: headers});
  }

  public getMarketingSoftware(): Observable<EncompassTreeModel> {
    let url = this.urlBase + 'documents/marketing/software';
    return this.http.get<EncompassTreeModel>(url, {headers: this.getRequestHeaders()});
  }

  public downloadMarketingSoftware(fileName: string): Observable<Blob> {
    let url = this.urlBase + 'documents/marketing/software/download?fileName=' + encodeURIComponent(fileName);
    return this.downloadViaGet(url, this.getRequestHeaders());
  }

  public getRevitFiles(): Observable<EncompassTreeModel> {
    let url = this.urlBase + 'documents/marketing/revit';
    return this.http.get<EncompassTreeModel>(url, {headers: this.getRequestHeaders()});
  }

  public downloadRevitFile(fileName: string): Observable<Blob> {
    let url = this.urlBase + 'documents/marketing/revit/download?fileName=' + encodeURIComponent(fileName);
    return this.downloadViaGet(url, this.getRequestHeaders());
  }

  getPresentationFiles(): Observable<EncompassTreeModel> {
    const url = this.urlBase + 'documents/marketing/presentations';
    return this.http.get<EncompassTreeModel>(url, {headers: this.getRequestHeaders()});
  }

  public downloadPresentationFile(fileName: string): Observable<Blob> {
    const url = this.urlBase + 'documents/marketing/presentation/download?fileName=' + encodeURIComponent(fileName);
    return this.downloadViaGet(url, this.getRequestHeaders());
  }

  approveCreditDocument(document: CreditDocument): Observable<string> {
    const url: string = this.urlBase + 'documents/creditDocument/approve/' + document.entityKey.entityId + '/' + document.entityKey.entityRevision;
    const body: string = document.fileName;
    const headers: HttpHeaders = this.getRequestHeaders().set('Content-Type', 'text/plain');
    return this.http.post(url, body, {headers: headers, responseType: 'text'});
  }

  denyCreditDocument(document: CreditDocument): Observable<string> {
    const url: string = this.urlBase + 'documents/creditDocument/deny/' + document.entityKey.entityId + '/' + document.entityKey.entityRevision;
    const body: string = document.fileName;
    const headers: HttpHeaders = this.getRequestHeaders().set('Content-Type', 'text/plain');
    return this.http.post(url, body, {headers: headers, responseType: 'text'});
  }

  private getRequestHeaders(): HttpHeaders {
    return new HttpHeaders().set(GoogleService.TokenHeader, this.googleService.getAccessToken());
  }

  private downloadViaPost(url: string, document: Document): Observable<Blob> {
    let body = JSON.stringify(document);
    return this.http.post(url, body, {
      headers: this.getPostHeaders(),
      observe: 'response',
      responseType: 'blob'
    }).pipe(map((response: any) => {
      return new Blob([response.body], {type: response.headers.get('Content-Type')});
    }));
  }

  private getPostHeaders(): HttpHeaders {
    return this.getRequestHeaders().set("Content-Type", "application/json");
  }

  private downloadViaGet(url: string, headers: HttpHeaders): Observable<Blob> {
    return this.http.get(url, {
      headers: headers,
      observe: 'response',
      responseType: 'blob'
    }).pipe(map((response: any) => {
      return new Blob([response.body], {type: response.headers.get('Content-Type')});
    }));
  }

  sortNodes(nodes: EncompassTreeNode[]): EncompassTreeNode[] {
    for (const node of nodes) {
      if (node.hasChildren) {
        node.children = this.sortNodes(node.children);
      }
    }
    return nodes.sort((first, second) => first.displayName.trim().localeCompare(second.displayName.trim()));
  }

  getRevisionChangeReport(key: EntityKey): Observable<Blob> {
    let url = this.urlBase + 'documents/entityRevisionChangeReport';
    let body = JSON.stringify(key);
    return this.http.post(url, body, {
      headers: this.getPostHeaders(),
      observe: 'response',
      responseType: 'blob'
    }).pipe(map((response: any) => {
      return new Blob([response.body], { type: response.headers.get('Content-Type') });
    }));
  }

  getOpenSpecialQuoteRequestsReport(): Observable<Blob> {
    const url = this.urlBase + 'reports/openSpecialQuoteRequests';
    return this.downloadViaGet(url, this.getRequestHeaders());
  }

  getTodaysApprovedQuotesReport(): Observable<Blob> {
    const url = this.urlBase + 'reports/todaysApprovedQuotes';
    return this.downloadViaGet(url, this.getRequestHeaders());
  }

  getRepOfficeRosterReport(): Observable<Blob> {
    const url = this.urlBase + 'reports/repOfficeRoster';
    return this.downloadViaGet(url, this.getRequestHeaders());
  }
}

